/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ShieldCheck, LockKey, FingerprintSimple, FileText, CreditCard,
  Cloud, ArrowRight, Database, Gavel, Notebook, Key, CheckCircle,
} from "@phosphor-icons/react";
import {
  MarketingNav, MarketingFooter, Eyebrow, FeatureCard, BulletList,
  CtaBanner, BTN_GREEN,
} from "./_shared";

export default function SecurityCompliance() {
  useEffect(() => {
    document.title = "Security & Compliance · Rescigo Health";
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="min-h-screen bg-white" data-testid="page-security">
      <MarketingNav/>

      {/* HERO */}
      <section className="relative pt-36 pb-24 overflow-hidden bg-[#03090A] text-white">
        <div className="absolute inset-0 opacity-40">
          <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, #2D5A27 0%, transparent 40%), radial-gradient(circle at 70% 80%, #0A2540 0%, transparent 40%)" }}/>
          <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)", backgroundSize: "40px 40px" }}/>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-14 items-center">
          <div>
            <Eyebrow color="#CFE7C7">Security · Compliance · Data Integrity</Eyebrow>
            <h1 className="font-heading text-5xl md:text-6xl mt-4 leading-[1.05]">
              Enterprise-grade <span className="italic text-[#CFE7C7]">security</span> for regulated environments.
            </h1>
            <p className="text-lg text-white/70 mt-6 max-w-xl leading-relaxed">
              ISO 27001 controls, court-admissible audit trails, zero-trust
              architecture, and government-grade compliance — deployed from day one.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/#contact" data-testid="sec-cta-request" className={BTN_GREEN}>
                View security framework <ArrowRight size={14} weight="bold"/>
              </Link>
              <a href="mailto:care@rescigo.in"
                 className="inline-flex items-center gap-2 h-12 px-5 rounded-full border border-white/30 text-white hover:bg-white/10 text-sm font-medium">
                care@rescigo.in
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-3 max-w-md">
              <Badge t="ISO 27001" s="in progress"/>
              <Badge t="SOC 2" s="pipeline"/>
              <Badge t="BMW Rules" s="aligned"/>
            </div>
          </div>
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
                       className="relative">
            <LockArchitectureSVG/>
          </motion.div>
        </div>
      </section>

      {/* 6 CONTROLS */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl">
            <Eyebrow color="#0A2540">Security controls</Eyebrow>
            <h2 className="font-heading text-4xl md:text-5xl mt-4 text-slate-900">
              Defense-in-depth across every layer.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-12">
            <FeatureCard accent="#0A2540" Icon={FingerprintSimple} title="Role-Based Access Control" desc="12-tier role system with fine-grained permissions. Principle of least privilege enforced across backend + frontend."/>
            <FeatureCard accent="#0A2540" Icon={LockKey} title="Multi-Factor Authentication" desc="8-digit email OTP mandatory for all admin-panel users. 2FA cannot be disabled — policy-enforced."/>
            <FeatureCard accent="#2D5A27" Icon={Notebook} title="Immutable Audit Logs" desc="Every write-action signed and timestamped with actor, tenant, IP, and user-agent. Court-admissible evidence trail."/>
            <FeatureCard accent="#2D5A27" Icon={Database} title="QR Chain Traceability" desc="Tamper-evident QR bag chain-of-custody. Cryptographic binding between source, transit, and disposal events."/>
            <FeatureCard accent="#0A2540" Icon={Cloud} title="API Security" desc="HTTPS-only (TLS 1.3), per-tenant JWT scopes, rate-limiting, request-signing on webhooks (Svix HMAC-SHA256)."/>
            <FeatureCard accent="#2D5A27" Icon={CreditCard} title="Payment Security" desc="PCI-DSS-scoped flows via Razorpay. HMAC-SHA256 signature verification on every payment event. Zero card data in our DB."/>
          </div>
        </div>
      </section>

      {/* STANDARDS TABLE */}
      <section className="py-24 bg-[#F7F6F2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Eyebrow color="#0A2540">Standards & certifications</Eyebrow>
          <h2 className="font-heading text-4xl md:text-5xl mt-4 text-slate-900">Mapped to global frameworks.</h2>
          <div className="mt-10 bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-600">
                  <th className="p-4 text-left font-semibold">Framework</th>
                  <th className="p-4 text-left font-semibold">Category</th>
                  <th className="p-4 text-left font-semibold">Coverage</th>
                  <th className="p-4 text-left font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                <SRow f="ISO/IEC 27001" c="Information Security" cov="Full ISMS" s="in progress"/>
                <SRow f="SOC 2 Type II" c="Service Controls" cov="Security · Availability · Confidentiality" s="pipeline"/>
                <SRow f="GDPR" c="Data Privacy" cov="EU citizens & lawful basis" s="compliant"/>
                <SRow f="DPDP Act 2023" c="Data Privacy (India)" cov="Personal data handling" s="compliant"/>
                <SRow f="BMW Rules 2016" c="Biomedical Waste" cov="End-to-end chain" s="aligned"/>
                <SRow f="CPCB Guidelines" c="Environmental" cov="Disposal + manifest" s="aligned"/>
                <SRow f="IT Act 2000 · Sec 66" c="Cybersecurity" cov="Unauthorized access" s="compliant"/>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* DATA PRIVACY */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-14 items-start">
          <div>
            <Eyebrow color="#0A2540">Data privacy</Eyebrow>
            <h2 className="font-heading text-4xl md:text-5xl mt-4 text-slate-900">Your citizens' data. Your custody.</h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              We operate under strict tenant isolation. Your data never comingles
              with another organisation's. Every API call is scoped by tenant ID.
              Every admin action is audited with IP + device metadata.
            </p>
            <div className="mt-6"><BulletList color="#0A2540" items={[
              "Tenant-scoped data isolation at the database layer",
              "Field-level encryption for PII (phone, email, Aadhaar-ref)",
              "Data residency in India · MongoDB Atlas Mumbai region",
              "Annual third-party penetration testing (external firm)",
              "Breach notification SLA: 72 hours to affected parties",
              "Right-to-erasure workflow for DPDP/GDPR data subjects",
            ]}/></div>
          </div>
          <div className="bg-gradient-to-br from-[#03090A] to-[#0A2540] text-white rounded-2xl p-10 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#2D5A27]/20 rounded-full blur-3xl"/>
            <div className="relative">
              <Key size={36} weight="duotone" className="opacity-80"/>
              <div className="font-heading text-2xl mt-5 leading-tight">
                Responsible disclosure
              </div>
              <p className="text-sm text-white/70 mt-3 leading-relaxed">
                Found a vulnerability? We run a responsible-disclosure program with
                48-hour triage and rewards for valid reports. We never prosecute
                good-faith researchers.
              </p>
              <div className="mt-6 p-4 bg-white/5 rounded-lg border border-white/10">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">Coordinated disclosure</div>
                <div className="font-mono text-sm mt-2 text-white">care@rescigo.in</div>
                <div className="text-[11px] text-white/60 mt-1">PGP key · keys.rescigo.health/security.asc</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CtaBanner
        eyebrow="Security Framework"
        title="Due-diligence ready, from day one."
        body="Enterprise prospects and government procurement teams can request our full security package under mutual NDA."
        primary={{ label: "Request security pack", to: "/#contact", testid: "sec-cta-bottom" }}
        secondary={{ label: "care@rescigo.in", to: "mailto:care@rescigo.in" }}
      />
      <MarketingFooter/>
    </div>
  );
}

const Badge = ({ t, s }) => {
  const tone = s === "compliant" || s === "aligned" ? "emerald" : "amber";
  const cls = tone === "emerald" ? "text-emerald-300 bg-emerald-500/10 ring-emerald-500/30" : "text-amber-300 bg-amber-500/10 ring-amber-500/30";
  return (
    <div className={`rounded-lg ring-1 p-2.5 ${cls}`}>
      <div className="text-[10px] uppercase tracking-[0.15em] font-semibold">{t}</div>
      <div className="text-[9px] uppercase tracking-[0.15em] opacity-70 mt-0.5">{s}</div>
    </div>
  );
};

const SRow = ({ f, c, cov, s }) => {
  const tone = s === "compliant" || s === "aligned" ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800";
  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50/50">
      <td className="p-4 font-medium text-slate-900">{f}</td>
      <td className="p-4 text-slate-600">{c}</td>
      <td className="p-4 text-slate-600">{cov}</td>
      <td className="p-4">
        <span className={`px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-[0.1em] font-semibold ${tone}`}>
          {s}
        </span>
      </td>
    </tr>
  );
};

const LockArchitectureSVG = () => (
  <svg viewBox="0 0 400 400" className="w-full max-w-md">
    <defs>
      <linearGradient id="lg1" x1="0" x2="1" y1="0" y2="1">
        <stop offset="0%" stopColor="#2D5A27" stopOpacity="0.6"/>
        <stop offset="100%" stopColor="#0A2540" stopOpacity="0.6"/>
      </linearGradient>
    </defs>
    <circle cx="200" cy="200" r="160" fill="none" stroke="url(#lg1)" strokeWidth="1" strokeDasharray="2 4"/>
    <circle cx="200" cy="200" r="120" fill="none" stroke="url(#lg1)" strokeWidth="1" strokeDasharray="4 6"/>
    <circle cx="200" cy="200" r="80" fill="none" stroke="url(#lg1)" strokeWidth="1" strokeDasharray="6 8"/>
    {[0, 60, 120, 180, 240, 300].map((deg, i) => (
      <circle key={i} cx={200 + 160 * Math.cos(deg * Math.PI / 180)}
              cy={200 + 160 * Math.sin(deg * Math.PI / 180)}
              r="5" fill="#2D5A27" opacity="0.8">
        <animate attributeName="opacity" values="0.3;1;0.3" dur="3s" repeatCount="indefinite" begin={`${i * 0.5}s`}/>
      </circle>
    ))}
    <g transform="translate(200,200)">
      <rect x="-30" y="-20" width="60" height="45" rx="8" fill="#0A2540"/>
      <path d="M -15,-20 L -15,-35 Q -15,-50 0,-50 Q 15,-50 15,-35 L 15,-20" fill="none" stroke="#CFE7C7" strokeWidth="4" strokeLinecap="round"/>
      <circle cx="0" cy="2" r="4" fill="#2D5A27"/>
      <rect x="-2" y="2" width="4" height="10" fill="#2D5A27"/>
    </g>
  </svg>
);
